import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClerkAuthProvider } from "@/context/ClerkAuthProvider";
import StudioShell from "@/components/layout/StudioShell";
import { Spinner } from "@/components/shared";

import Login from "@/pages/Login";
import ClerkLogin from "@/pages/ClerkLogin";
import EditorStudio from "@/pages/EditorStudio";
import Home from "@/pages/Home";
import Create from "@/pages/Create";
import ProductionDashboard from "@/pages/ProductionDashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Templates from "@/pages/Templates";
import PromptLibrary from "@/pages/PromptLibrary";
import VoiceStudio from "@/pages/VoiceStudio";
import SoundLibrary from "@/pages/SoundLibrary";
import BrandStudio from "@/pages/BrandStudio";
import VideoEngine from "@/pages/VideoEngine";
import CinematicPlanner from "@/pages/CinematicPlanner";
import ExportCenter from "@/pages/ExportCenter";
import Publishing from "@/pages/Publishing";
import Assets from "@/pages/Assets";
import Storage from "@/pages/Storage";
import ActivityPage from "@/pages/Activity";
import SettingsPage from "@/pages/Settings";
import Membership from "@/pages/Membership";
import Help from "@/pages/Help";
import { TermsOfService, PrivacyPolicy, RefundPolicy, ContactUs, AboutVyom } from "@/pages/Legal";
import Welcome from "@/pages/Welcome";
import AdminDashboard from "@/pages/AdminDashboard";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="vyom-bg min-h-screen grid place-items-center">
        <Spinner label="Entering Vyom Studio…" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicEntry() {
  const { user } = useAuth();
  return user ? <Navigate to="/studio" replace /> : <Welcome />;
}

function App() {
  // Clerk is primary when configured; otherwise legacy email/password.
  const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
  const AuthShell = CLERK_ENABLED ? ClerkAuthProvider : AuthProvider;
  const LoginPage = CLERK_ENABLED ? ClerkLogin : Login;
  return (
    <AuthShell>
      <Toaster theme="dark" position="top-right" richColors />
      <Routes>
        {/* The public welcome page is the canonical site entry point. */}
        <Route path="/" element={<PublicEntry />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Public compliance/legal pages -- outside <Protected>, reachable while logged out. */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<AboutVyom />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route
          path="/editor"
          element={
            <Protected>
              <EditorStudio />
            </Protected>
          }
        />
        <Route
          element={
            <Protected>
              <StudioShell />
            </Protected>
          }
        >
          <Route path="/studio" element={<Home />} />
          <Route path="/create" element={<Create />} />
          <Route path="/production" element={<ProductionDashboard />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/prompts" element={<PromptLibrary />} />
          <Route path="/voice-studio" element={<VoiceStudio />} />
          <Route path="/sound-library" element={<SoundLibrary />} />
          <Route path="/brand-studio" element={<BrandStudio />} />
          <Route path="/video-engine" element={<VideoEngine />} />
          <Route path="/cinematic-planner" element={<CinematicPlanner />} />
          <Route path="/export-center" element={<ExportCenter />} />
          <Route path="/publishing" element={<Publishing />} />
          <Route path="/assets" element={<Assets />} />
          <Route path="/storage" element={<Storage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<Membership />} />
          <Route path="/help" element={<Help />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthShell>
  );
}

export default App;
